import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import {
  api,
  DOC_AUDIENCES,
  DOC_LINK_KINDS,
  type DocAudience,
  type DocFile,
  type DocLanguage,
  type DocLink,
  type DocPage,
  type DocPageSummary,
  type DocRevision,
  type Project,
  type ProjectDocs,
} from "../../api";
import { Markdown } from "../../components/Markdown";
import { relative } from "../../components/ui";

// The question: how is this project used, and where are its guides and videos?

const LANG_WORD: Record<DocLanguage, string> = { en: "English", ar: "العربية" };
const AUDIENCE_WORD: Record<DocAudience, string> = { "end-user": "End users", admin: "Admins", developer: "Developers" };

export function bytes(n: number): string {
  if (n >= 1024 ** 3) return `${+(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${+(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function useDocs(slug: string) {
  return useQuery({ queryKey: ["docs", slug], queryFn: () => api<ProjectDocs>(`/projects/${slug}/docs`) });
}

function usePage(slug: string, page: string | null) {
  return useQuery({
    queryKey: ["doc-page", slug, page],
    queryFn: () => api<DocPage>(`/projects/${slug}/docs/pages/${page}`),
    enabled: Boolean(page),
  });
}

/** Presigned links last ten minutes: fetched on click, never kept. */
export async function fileUrl(slug: string, id: string, download = false) {
  return (await api<{ url: string }>(`/projects/${slug}/docs/files/${id}/url${download ? "?download=1" : ""}`)).url;
}

export function OpenFile({ slug, file, label = "Open" }: { slug: string; file: DocFile; label?: string }) {
  const [err, setErr] = useState("");
  const open = async () => {
    // A window opened in the click itself is not blocked; it is pointed at the signed URL after.
    const w = window.open("", "_blank");
    try {
      const url = await fileUrl(slug, file.id);
      if (w) { w.opener = null; w.location.href = url; } else window.location.assign(url);
    } catch (e) {
      w?.close();
      setErr((e as Error).message);
    }
  };
  return <span><button className="btn ghost small" onClick={open}>{label}</button>{err && <span className="error small"> {err}</span>}</span>;
}

/** An uploaded video: plays from a short-lived signed URL fetched when asked. */
export function FileVideo({ slug, file }: { slug: string; file: DocFile }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState("");
  if (src) return <video className="video-frame" controls autoPlay preload="metadata" src={src} onError={() => { setSrc(null); setErr("Couldn't play it; try again."); }} />;
  return (
    <button className="video-frame video-poster" onClick={async () => { try { setSrc(await fileUrl(slug, file.id)); } catch (e) { setErr((e as Error).message); } }}>
      <span className="play" aria-hidden="true">▶</span>
      <span className="small">{err || `Play · ${bytes(file.sizeBytes)}`}</span>
    </button>
  );
}

export function LinkEmbed({ link }: { link: DocLink }) {
  if (!link.embedUrl) return null;
  return (
    <iframe
      className="video-frame"
      src={link.embedUrl}
      title={link.title}
      loading="lazy"
      allow="encrypted-media; fullscreen; picture-in-picture"
      allowFullScreen
      referrerPolicy="strict-origin-when-cross-origin"
      sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
    />
  );
}

function Meta({ audience, language, extra }: { audience: DocAudience; language: DocLanguage; extra?: string | null }) {
  return <div className="cell-sub">{AUDIENCE_WORD[audience]} · {LANG_WORD[language]}{extra ? ` · ${extra}` : ""}</div>;
}

/* ---------------- forms ---------------- */

function PageEditor({ project, page, guide, done }: { project: Project; page?: DocPage; guide?: boolean; done: (slug?: string) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    title: page?.title ?? (guide ? "User guide" : ""),
    slug: page?.slug ?? "",
    body: page?.body ?? "",
    audience: page?.audience ?? "end-user",
    language: page?.language ?? "en",
    env: page?.env ?? "",
    isGuide: page?.isGuide ?? Boolean(guide),
  });
  const [preview, setPreview] = useState(false);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, slug: f.slug.trim() || undefined, env: f.env || null };
      return page
        ? api<DocPage>(`/projects/${project.slug}/docs/pages/${page.slug}`, { method: "PATCH", body: JSON.stringify({ ...body, baseUpdatedAt: page.updatedAt }) })
        : api<DocPage>(`/projects/${project.slug}/docs/pages`, { method: "POST", body: JSON.stringify(body) });
    },
    onSuccess: (p) => {
      qc.invalidateQueries({ queryKey: ["docs", project.slug] });
      qc.invalidateQueries({ queryKey: ["doc-page", project.slug] });
      qc.invalidateQueries({ queryKey: ["doc-revisions", project.slug] });
      qc.invalidateQueries({ queryKey: ["all-docs"] });
      done(p.slug);
    },
  });
  const rtl = f.language === "ar";
  return (
    <form className="panel" style={{ display: "grid", gap: 16 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <h3>{page ? `Edit ${page.title}` : f.isGuide ? "New user guide" : "New page"}</h3>
      <div className="form-grid">
        <label className="field"><span>Title</span><input className="input" dir={rtl ? "rtl" : "ltr"} value={f.title} onChange={set("title")} required maxLength={200} /></label>
        <label className="field"><span>Slug</span><input className="input mono" value={f.slug} onChange={set("slug")} placeholder={page ? "" : "made from the title"} pattern="[a-z0-9]+(-[a-z0-9]+)*" maxLength={64} /><span className="hint">Lowercase letters and digits joined by hyphens; part of the page's link.</span></label>
        <label className="field"><span>Audience</span>
          <select className="input" value={f.audience} onChange={set("audience")}>{DOC_AUDIENCES.map((a) => <option key={a} value={a}>{AUDIENCE_WORD[a]}</option>)}</select>
        </label>
        <label className="field"><span>Language</span>
          <select className="input" value={f.language} onChange={set("language")}><option value="en">English</option><option value="ar">العربية (Arabic)</option></select>
        </label>
        <label className="field"><span>Environment</span>
          <select className="input" value={f.env} onChange={set("env")}><option value="">Any</option>{project.environments.map((e) => <option key={e.id}>{e.name}</option>)}</select>
        </label>
        <label className="field check-field"><input type="checkbox" checked={f.isGuide} onChange={(e) => setF({ ...f, isGuide: e.target.checked })} /><span>The user guide in this language<span className="hint" style={{ display: "block", fontWeight: 400 }}>Shown first. Replaces the current guide in this language, if any.</span></span></label>
      </div>
      <div className="field">
        <div className="spread"><span style={{ fontWeight: 500 }}>Body</span>
          <div className="segmented" role="group" aria-label="Editor view">
            <button type="button" className="btn small" aria-pressed={!preview} onClick={() => setPreview(false)}>Write</button>
            <button type="button" className="btn small" aria-pressed={preview} onClick={() => setPreview(true)}>Preview</button>
          </div>
        </div>
        {preview ? <div className="panel"><Markdown text={f.body} language={f.language} /></div>
          : <textarea className="input mono doc-editor" dir={rtl ? "rtl" : "ltr"} value={f.body} onChange={set("body")} placeholder={"# Getting started\n\n1. Sign in at …\n2. …"} />}
        <span className="hint">Markdown: # headings, **bold**, lists, [links](https://…), tables, `code`. Every save is kept in the page's history.</span>
      </div>
      {save.error && <p className="error">{save.error.message}</p>}
      <div className="actions"><button className="btn primary" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</button><button type="button" className="btn" onClick={() => done()}>Cancel</button></div>
    </form>
  );
}

function LinkForm({ project, link, done }: { project: Project; link?: DocLink; done: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: link?.title ?? "", url: link?.url ?? "", kind: link?.kind ?? "video", audience: link?.audience ?? "end-user", language: link?.language ?? "en", notes: link?.notes ?? "" });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const save = useMutation({
    mutationFn: () => link
      ? api(`/projects/${project.slug}/docs/links/${link.id}`, { method: "PATCH", body: JSON.stringify(f) })
      : api(`/projects/${project.slug}/docs/links`, { method: "POST", body: JSON.stringify(f) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["docs", project.slug] }); qc.invalidateQueries({ queryKey: ["all-docs"] }); done(); },
  });
  return (
    <form className="panel" style={{ display: "grid", gap: 12 }} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <h3>{link ? "Edit link" : "Add a link"}</h3>
      <div className="form-grid">
        <label className="field"><span>Title</span><input className="input" value={f.title} onChange={set("title")} required maxLength={200} /></label>
        <label className="field"><span>URL</span><input className="input" type="url" value={f.url} onChange={set("url")} required placeholder="https://www.youtube.com/watch?v=…" /><span className="hint">YouTube, Loom and Google Drive videos play here.</span></label>
        <label className="field"><span>Kind</span><select className="input" value={f.kind} onChange={set("kind")}>{DOC_LINK_KINDS.map((k) => <option key={k}>{k}</option>)}</select></label>
        <label className="field"><span>Audience</span><select className="input" value={f.audience} onChange={set("audience")}>{DOC_AUDIENCES.map((a) => <option key={a} value={a}>{AUDIENCE_WORD[a]}</option>)}</select></label>
        <label className="field"><span>Language</span><select className="input" value={f.language} onChange={set("language")}><option value="en">English</option><option value="ar">العربية (Arabic)</option></select></label>
        <label className="field"><span>Notes</span><input className="input" value={f.notes} onChange={set("notes")} maxLength={2000} /></label>
      </div>
      {save.error && <p className="error">{save.error.message}</p>}
      <div className="actions"><button className="btn primary" disabled={save.isPending}>{link ? "Save" : "Add"}</button><button type="button" className="btn" onClick={done}>Cancel</button></div>
    </form>
  );
}

/** PUT with progress: fetch can't report upload progress, XMLHttpRequest can. */
function put(url: string, file: File, headers: Record<string, string>, onProgress: (n: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const x = new XMLHttpRequest();
    x.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) x.setRequestHeader(k, v);
    x.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    x.onload = () => (x.status >= 200 && x.status < 300 ? resolve() : reject(new Error(`storage refused the upload (${x.status})`)));
    x.onerror = () => reject(new Error("the upload was interrupted"));
    x.send(file);
  });
}

function UploadForm({ project, maxBytes, done }: { project: Project; maxBytes: number; done: () => void }) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [f, setF] = useState({ title: "", audience: "end-user", language: "en", notes: "" });
  const [progress, setProgress] = useState<number | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });
  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("choose a file");
      if (file.size > maxBytes) throw new Error(`the file is ${bytes(file.size)}; the limit is ${bytes(maxBytes)}`);
      const r = await api<{ file: DocFile; upload: { url: string; headers: Record<string, string> } }>(`/projects/${project.slug}/docs/files`, {
        method: "POST",
        body: JSON.stringify({ ...f, title: f.title || file.name, filename: file.name, contentType: file.type, size: file.size }),
      });
      setProgress(0);
      await put(r.upload.url, file, r.upload.headers, setProgress);
      await api(`/projects/${project.slug}/docs/files/${r.file.id}/complete`, { method: "POST" });
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["docs", project.slug] }); qc.invalidateQueries({ queryKey: ["all-docs"] }); done(); },
    onSettled: () => setProgress(null),
  });
  return (
    <form className="panel" style={{ display: "grid", gap: 12 }} onSubmit={(e) => { e.preventDefault(); upload.mutate(); }}>
      <h3>Upload a file</h3>
      <label className="field"><span>File</span>
        <input className="input" type="file" accept="video/*,application/pdf,image/png,image/jpeg,image/gif,image/webp,image/avif" onChange={(e) => setFile(e.target.files?.[0] ?? null)} required style={{ paddingTop: 8 }} />
        <span className="hint">Videos, PDFs and images, up to {bytes(maxBytes)}.{file ? ` This one: ${bytes(file.size)}.` : ""}</span>
      </label>
      <div className="form-grid">
        <label className="field"><span>Title</span><input className="input" value={f.title} onChange={set("title")} placeholder={file?.name ?? ""} maxLength={200} /></label>
        <label className="field"><span>Notes</span><input className="input" value={f.notes} onChange={set("notes")} maxLength={2000} /></label>
        <label className="field"><span>Audience</span><select className="input" value={f.audience} onChange={set("audience")}>{DOC_AUDIENCES.map((a) => <option key={a} value={a}>{AUDIENCE_WORD[a]}</option>)}</select></label>
        <label className="field"><span>Language</span><select className="input" value={f.language} onChange={set("language")}><option value="en">English</option><option value="ar">العربية (Arabic)</option></select></label>
      </div>
      {progress !== null && <div className="progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}><span style={{ width: `${progress * 100}%` }} /></div>}
      {upload.error && <p className="error">{upload.error.message}</p>}
      <div className="actions">
        <button className="btn primary" disabled={upload.isPending}>{progress !== null ? `Uploading ${Math.round(progress * 100)}%` : upload.isPending ? "Preparing…" : "Upload"}</button>
        <button type="button" className="btn" onClick={done} disabled={upload.isPending}>Cancel</button>
      </div>
    </form>
  );
}

/* ---------------- one page ---------------- */

function History({ project, page, onRestore }: { project: Project; page: DocPage; onRestore: (body: string, title: string) => void }) {
  const q = useQuery({ queryKey: ["doc-revisions", project.slug, page.slug], queryFn: () => api<DocRevision[]>(`/projects/${project.slug}/docs/pages/${page.slug}/revisions`) });
  const [open, setOpen] = useState<string | null>(null);
  const rev = useQuery({
    queryKey: ["doc-revision", project.slug, page.slug, open],
    queryFn: () => api<DocRevision & { body: string }>(`/projects/${project.slug}/docs/pages/${page.slug}/revisions/${open}`),
    enabled: Boolean(open),
  });
  return (
    <section className="panel flush" aria-label="History">
      <div className="panel-head"><h3>History</h3><span className="stamp">{q.data ? `${q.data.length} versions` : ""}</span></div>
      {q.error && <div className="item"><span className="error">{q.error.message}</span></div>}
      <div className="list">
        {q.data?.map((r, i) => (
          <div key={r.id} className="item">
            <div>
              <div>{new Date(r.editedAt).toLocaleString()} {i === 0 && <span className="pill">current</span>}</div>
              <div className="cell-sub">{r.editedBy ?? "—"} · {r.title} · {r.length.toLocaleString()} characters</div>
            </div>
            <button className="btn ghost small" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? "Hide" : "View"}</button>
            {open === r.id && (
              <div style={{ gridColumn: "1 / -1", display: "grid", gap: 8 }}>
                {rev.data ? <><div className="panel"><Markdown text={rev.data.body} language={rev.data.language} /></div>
                  {i > 0 && <div><button className="btn small" onClick={() => onRestore(rev.data.body, rev.data.title)}>Edit from this version</button></div>}</>
                  : <span className="skeleton" style={{ width: 200, height: 14 }} />}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

function PageView({ project, slug, mayEdit, back }: { project: Project; slug: string; mayEdit: boolean; back: () => void }) {
  const qc = useQueryClient();
  const [, setParams] = useSearchParams();
  const q = usePage(project.slug, slug);
  const [editing, setEditing] = useState<DocPage | null>(null);
  const [history, setHistory] = useState(false);
  const remove = useMutation({
    mutationFn: () => api(`/projects/${project.slug}/docs/pages/${slug}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["docs", project.slug] }); qc.invalidateQueries({ queryKey: ["all-docs"] }); back(); },
  });
  if (q.isPending) return <div className="panel"><span className="skeleton" style={{ width: 300, height: 16 }} /></div>;
  if (q.error) return <div className="panel"><p className="error">{q.error.message}</p><button className="btn small" onClick={back}>Back to docs</button></div>;
  const p = q.data;
  if (editing) {
    return <PageEditor project={project} page={editing} done={(s) => { setEditing(null); if (s && s !== slug) setParams({ tab: "docs", page: s }); }} />;
  }
  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="toolbar">
        <button className="btn ghost small" onClick={back}>← All docs</button>
        {mayEdit && (
          <div className="actions">
            <button className="btn small" onClick={() => setEditing(p)}>Edit</button>
            <button className="btn ghost small" onClick={() => setHistory(!history)}>{history ? "Hide history" : "History"}</button>
            <button className="btn ghost small" disabled={remove.isPending} onClick={() => { if (confirm(`Delete “${p.title}” and its history?`)) remove.mutate(); }}>Delete</button>
          </div>
        )}
        {!mayEdit && <button className="btn ghost small" onClick={() => setHistory(!history)}>{history ? "Hide history" : "History"}</button>}
      </div>
      {remove.error && <p className="error">{remove.error.message}</p>}
      <article className="panel doc-page">
        <div style={{ marginBottom: 16 }} dir={p.language === "ar" ? "rtl" : "ltr"}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <h2 lang={p.language}>{p.title}</h2>
            {p.isGuide && <span className="pill positive">User guide</span>}
            <span className="pill">{LANG_WORD[p.language]}</span>
            {p.env && <span className="pill mono">{p.env}</span>}
          </div>
          <p className="stamp" dir="ltr" style={{ marginTop: 4 }}>{AUDIENCE_WORD[p.audience]} · last edited by {p.updatedBy ?? "—"}, {relative(p.updatedAt)} ({new Date(p.updatedAt).toLocaleString()})</p>
        </div>
        <Markdown text={p.body} language={p.language} />
      </article>
      {history && <History project={project} page={p} onRestore={(body, title) => { setHistory(false); setEditing({ ...p, body, title }); }} />}
    </div>
  );
}

/* ---------------- the tab ---------------- */

function GuideCard({ project, guides, openPage }: { project: Project; guides: DocPageSummary[]; openPage: (slug: string) => void }) {
  const [lang, setLang] = useState<DocLanguage>(guides.find((g) => g.language === "en") ? "en" : guides[0]?.language ?? "en");
  const guide = guides.find((g) => g.language === lang) ?? guides[0];
  const q = usePage(project.slug, guide?.slug ?? null);
  if (!guide) return null;
  return (
    <section className="panel doc-page" aria-labelledby="guide">
      <div className="spread" style={{ marginBottom: 12 }}>
        <div>
          <h2 id="guide">{guide.title}</h2>
          <p className="stamp">User guide · last edited by {guide.updatedBy ?? "—"}, {relative(guide.updatedAt)}</p>
        </div>
        <div className="actions">
          {guides.length > 1 && (
            <div className="segmented" role="group" aria-label="Guide language">
              {guides.map((g) => <button key={g.id} className="btn small" aria-pressed={g.language === lang} onClick={() => setLang(g.language)}>{LANG_WORD[g.language]}</button>)}
            </div>
          )}
          <button className="btn ghost small" onClick={() => openPage(guide.slug)}>Open</button>
        </div>
      </div>
      {q.data ? <div className="doc-guide-body"><Markdown text={q.data.body} language={q.data.language} /></div> : q.error ? <p className="error">{q.error.message}</p> : <span className="skeleton" style={{ width: 300, height: 16 }} />}
    </section>
  );
}

function SearchResults({ project, q, openPage }: { project: Project; q: string; openPage: (slug: string) => void }) {
  const r = useQuery({
    queryKey: ["doc-search", project.slug, q],
    queryFn: () => api<{ pages: (Pick<DocPageSummary, "slug" | "title" | "language" | "audience" | "isGuide"> & { snippet: string })[]; links: DocLink[]; files: DocFile[] }>(
      `/projects/${project.slug}/docs/search?q=${encodeURIComponent(q)}`,
    ),
    placeholderData: (p) => p,
  });
  if (r.error) return <p className="error">{r.error.message}</p>;
  if (!r.data) return <span className="skeleton" style={{ width: 240, height: 16 }} />;
  const n = r.data.pages.length + r.data.links.length + r.data.files.length;
  return (
    <section className="panel flush" aria-label="Search results">
      <div className="panel-head"><h3>{n} result{n === 1 ? "" : "s"} for “{q}”</h3></div>
      <div className="list">
        {r.data.pages.map((p) => (
          <div key={p.slug} className="item">
            <div>
              <button className="link-button cell-main" onClick={() => openPage(p.slug)} dir={p.language === "ar" ? "rtl" : "ltr"}>{p.title}</button>
              {p.isGuide && <span className="pill positive" style={{ marginLeft: 8 }}>User guide</span>}
              <div className="cell-sub" dir={p.language === "ar" ? "rtl" : "ltr"}>{p.snippet}</div>
            </div>
            <span className="muted small">page</span>
          </div>
        ))}
        {r.data.links.map((l) => (
          <div key={l.id} className="item">
            <div><a className="cell-main" href={l.url} target="_blank" rel="noopener noreferrer">{l.title}</a><div className="cell-sub">{l.url.replace(/^https?:\/\//, "")}</div></div>
            <span className="muted small">{l.kind}</span>
          </div>
        ))}
        {r.data.files.map((f) => (
          <div key={f.id} className="item">
            <div><span className="cell-main">{f.title}</span><div className="cell-sub">{f.filename} · {bytes(f.sizeBytes)}</div></div>
            <OpenFile slug={project.slug} file={f} />
          </div>
        ))}
        {n === 0 && <div className="item"><span className="muted">Nothing matches.</span></div>}
      </div>
    </section>
  );
}

export function DocsTab({ project }: { project: Project }) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const page = params.get("page");
  const docs = useDocs(project.slug);
  const [mode, setMode] = useState<null | "page" | "guide" | "link" | "upload">(null);
  const [editLink, setEditLink] = useState<DocLink | null>(null);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [audience, setAudience] = useState<"" | DocAudience>("");
  const [language, setLanguage] = useState<"" | DocLanguage>("");
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  const openPage = (slug: string) => { setMode(null); setParams({ tab: "docs", page: slug }); };
  const back = () => setParams({ tab: "docs" });
  const removeLink = useMutation({
    mutationFn: (id: string) => api(`/projects/${project.slug}/docs/links/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["docs", project.slug] }); qc.invalidateQueries({ queryKey: ["all-docs"] }); },
  });
  const removeFile = useMutation({
    mutationFn: (id: string) => api(`/projects/${project.slug}/docs/files/${id}`, { method: "DELETE" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["docs", project.slug] }); qc.invalidateQueries({ queryKey: ["all-docs"] }); },
  });

  const shown = useMemo(() => {
    const keep = (x: { audience: DocAudience; language: DocLanguage }) => (!audience || x.audience === audience) && (!language || x.language === language);
    const d = docs.data;
    return {
      guides: (d?.pages ?? []).filter((p) => p.isGuide && keep(p)),
      pages: (d?.pages ?? []).filter((p) => !p.isGuide && keep(p)),
      videos: [
        ...(d?.links ?? []).filter((l) => l.embedUrl && keep(l)).map((l) => ({ link: l, file: undefined })),
        ...(d?.files ?? []).filter((f) => f.kind === "video" && f.status === "ready" && keep(f)).map((f) => ({ link: undefined, file: f })),
      ] as { link?: DocLink; file?: DocFile }[],
      links: (d?.links ?? []).filter((l) => !l.embedUrl && keep(l)),
      files: (d?.files ?? []).filter((f) => (f.kind !== "video" || f.status !== "ready") && keep(f)),
    };
  }, [docs.data, audience, language]);

  if (docs.isPending) return <div className="panel"><span className="skeleton" style={{ width: 300, height: 16 }} /></div>;
  if (docs.error) return <div className="panel error">{docs.error.message}</div>;
  const d = docs.data;
  const mayEdit = d.mayEdit;

  if (page) return <PageView project={project} slug={page} mayEdit={mayEdit} back={back} />;

  const empty = d.pages.length + d.links.length + d.files.length === 0;
  const remove = (what: string, run: () => void) => { if (confirm(`Delete ${what}?`)) run(); };

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="toolbar">
        <div className="actions" style={{ flexWrap: "wrap" }}>
          <input className="search-field" type="search" aria-label="Search the docs" placeholder="Search pages, links and files" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="input" style={{ height: 32, width: 150 }} aria-label="Audience" value={audience} onChange={(e) => setAudience(e.target.value as DocAudience | "")}>
            <option value="">Every audience</option>{DOC_AUDIENCES.map((a) => <option key={a} value={a}>{AUDIENCE_WORD[a]}</option>)}
          </select>
          <select className="input" style={{ height: 32, width: 130 }} aria-label="Language" value={language} onChange={(e) => setLanguage(e.target.value as DocLanguage | "")}>
            <option value="">Any language</option><option value="en">English</option><option value="ar">العربية</option>
          </select>
        </div>
        {mayEdit && !mode && (
          <div className="actions" style={{ flexWrap: "wrap" }}>
            <button className="btn" onClick={() => setMode("page")}>New page</button>
            <button className="btn" onClick={() => setMode("link")}>Add link</button>
            {d.uploads.enabled && <button className="btn" onClick={() => setMode("upload")}>Upload file</button>}
          </div>
        )}
      </div>

      {mode === "page" && <PageEditor project={project} done={(s) => { setMode(null); if (s) openPage(s); }} />}
      {mode === "guide" && <PageEditor project={project} guide done={(s) => { setMode(null); if (s) openPage(s); }} />}
      {mode === "link" && <LinkForm project={project} done={() => setMode(null)} />}
      {editLink && <LinkForm project={project} link={editLink} done={() => setEditLink(null)} />}
      {mode === "upload" && <UploadForm project={project} maxBytes={d.uploads.maxBytes} done={() => setMode(null)} />}

      {term.length >= 2 ? <SearchResults project={project} q={term} openPage={openPage} /> : (
        <>
          {empty && !mode && (
            <div className="empty">
              <div>
                <p>No docs for {project.name} yet.</p>
                {mayEdit && <p style={{ marginTop: 8 }}><button className="btn small" onClick={() => setMode("guide")}>Write the user guide</button></p>}
              </div>
            </div>
          )}
          {!empty && shown.guides.length === 0 && mayEdit && !mode && (
            <div className="empty"><span>No user guide yet. <button className="btn ghost small" onClick={() => setMode("guide")}>Write one</button></span></div>
          )}
          {shown.guides.length > 0 && <GuideCard key={shown.guides.map((g) => g.id).join()} project={project} guides={shown.guides} openPage={openPage} />}

          {shown.videos.length > 0 && (
            <section aria-labelledby="videos">
              <div className="section-head"><h2 id="videos">Videos</h2></div>
              <div className="video-grid">
                {shown.videos.map(({ link: l, file: f }) => l ? (
                  <figure key={l.id} className="video-card">
                    <LinkEmbed link={l} />
                    <figcaption>
                      <div className="spread">
                        <a className="cell-main" href={l.url} target="_blank" rel="noopener noreferrer" dir={l.language === "ar" ? "rtl" : "ltr"}>{l.title}</a>
                        {mayEdit && <span className="actions"><button className="btn ghost small" onClick={() => setEditLink(l)}>Edit</button><button className="btn ghost small" onClick={() => remove(`“${l.title}”`, () => removeLink.mutate(l.id))}>Delete</button></span>}
                      </div>
                      <Meta audience={l.audience} language={l.language} extra={l.notes} />
                    </figcaption>
                  </figure>
                ) : f ? (
                  <figure key={f.id} className="video-card">
                    <FileVideo slug={project.slug} file={f} />
                    <figcaption>
                      <div className="spread">
                        <span className="cell-main" dir={f.language === "ar" ? "rtl" : "ltr"}>{f.title}</span>
                        {mayEdit && <button className="btn ghost small" onClick={() => remove(`“${f.title}”`, () => removeFile.mutate(f.id))}>Delete</button>}
                      </div>
                      <Meta audience={f.audience} language={f.language} extra={f.notes || `uploaded by ${f.uploadedBy ?? "—"}`} />
                    </figcaption>
                  </figure>
                ) : null)}
              </div>
            </section>
          )}

          <div className="docs-lists">
            {(shown.pages.length > 0 || mayEdit) && (
              <section className="panel flush" aria-labelledby="wiki">
                <div className="panel-head"><h3 id="wiki">Wiki</h3><span className="stamp">{shown.pages.length} page{shown.pages.length === 1 ? "" : "s"}</span></div>
                <div className="list">
                  {shown.pages.map((p) => (
                    <div key={p.id} className="item">
                      <div>
                        <button className="link-button cell-main" onClick={() => openPage(p.slug)} dir={p.language === "ar" ? "rtl" : "ltr"}>{p.title}</button>
                        <Meta audience={p.audience} language={p.language} extra={`${p.env ? `${p.env} · ` : ""}edited by ${p.updatedBy ?? "—"} ${relative(p.updatedAt)}`} />
                      </div>
                    </div>
                  ))}
                  {shown.pages.length === 0 && <div className="item"><span className="muted">No pages{audience || language ? " match" : " yet"}.</span></div>}
                </div>
              </section>
            )}
            {shown.links.length > 0 && (
              <section className="panel flush" aria-labelledby="links">
                <div className="panel-head"><h3 id="links">Links</h3></div>
                <div className="list">
                  {shown.links.map((l) => (
                    <div key={l.id} className="item">
                      <div>
                        <a className="cell-main" href={l.url} target="_blank" rel="noopener noreferrer" dir={l.language === "ar" ? "rtl" : "ltr"}>{l.title}</a> <span className="pill">{l.kind}</span>
                        <Meta audience={l.audience} language={l.language} extra={l.notes || l.url.replace(/^https?:\/\//, "").slice(0, 60)} />
                      </div>
                      {mayEdit && <span className="actions"><button className="btn ghost small" onClick={() => setEditLink(l)}>Edit</button><button className="btn ghost small" onClick={() => remove(`“${l.title}”`, () => removeLink.mutate(l.id))}>Delete</button></span>}
                    </div>
                  ))}
                </div>
              </section>
            )}
            {shown.files.length > 0 && (
              <section className="panel flush" aria-labelledby="files">
                <div className="panel-head"><h3 id="files">Files</h3></div>
                <div className="list">
                  {shown.files.map((f) => (
                    <div key={f.id} className="item">
                      <div>
                        <span className="cell-main" dir={f.language === "ar" ? "rtl" : "ltr"}>{f.title}</span> {f.status === "pending" && <span className="pill warning">upload not finished</span>}
                        <Meta audience={f.audience} language={f.language} extra={`${f.kind ?? "file"} · ${bytes(f.sizeBytes)}${f.notes ? ` · ${f.notes}` : ""}`} />
                      </div>
                      <span className="actions">
                        {f.status === "ready" && <OpenFile slug={project.slug} file={f} />}
                        {mayEdit && <button className="btn ghost small" onClick={() => remove(`“${f.title}”`, () => removeFile.mutate(f.id))}>Delete</button>}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
          {(removeLink.error || removeFile.error) && <p className="error">{(removeLink.error ?? removeFile.error)?.message}</p>}
          {mayEdit && !d.uploads.enabled && <p className="stamp">File uploads are off: the lab has no LAB_DOCS_BUCKET set. Links and pages work.</p>}
        </>
      )}
    </div>
  );
}
